import {
  cloneElement,
  isValidElement,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  type ReactElement,
} from "react";
import { differenceInCalendarDays, getISOWeek } from "date-fns";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { PERSON_EVENT_COLOR_TONE_TOKENS } from "@/lib/personEventColorTones";
import { cn } from "@/lib/utils";
import type {
  AvailabilityInterval,
  AvailabilityRow,
  CalendarRange,
  CapacityDay,
  CapacityTeamSummary,
} from "./model";
import "./availability-capacity.css";

type GridStyle = CSSProperties & { "--capacity-visual-ratio"?: number };
type CapacityGridStyle = CSSProperties & {
  "--availability-capacity-day-count"?: number;
  "--availability-capacity-threshold-ratio"?: number;
};

interface VisibleInterval {
  interval: AvailabilityInterval;
  visibleFrom: string;
  visibleTo: string;
  start: number;
  end: number;
}

interface IntervalDescription {
  ariaLabel: string;
  tooltipLines: string[];
}

function clipInterval(
  interval: AvailabilityInterval,
  rangeStartKey: string | undefined,
  rangeEndKey: string | undefined,
  dayIndexByKey: Map<string, number>,
): VisibleInterval | null {
  if (rangeStartKey === undefined || rangeEndKey === undefined) return null;
  const visibleFrom = interval.fromKey < rangeStartKey ? rangeStartKey : interval.fromKey;
  const visibleTo = interval.toKey > rangeEndKey ? rangeEndKey : interval.toKey;
  const start = dayIndexByKey.get(visibleFrom);
  const end = dayIndexByKey.get(visibleTo);
  if (start === undefined || end === undefined || end < start) return null;
  return { interval, visibleFrom, visibleTo, start, end };
}

export interface AvailabilityCapacityGridProps {
  range: CalendarRange;
  rows: AvailabilityRow[];
  capacity: CapacityDay[];
  showCapacity: boolean;
  capacityWarningThresholdPct?: number;
  capacityTeamSummary?: CapacityTeamSummary;
  renderRowName?: (row: AvailabilityRow) => ReactNode;
  renderRowActions?: (row: AvailabilityRow) => ReactNode;
  onIntervalActivate?: (row: AvailabilityRow, interval: AvailabilityInterval) => void;
  legendItems?: AvailabilityLegendItem[];
}

export interface AvailabilityLegendItem {
  id: number | string;
  name: string;
  abbreviation?: string | null;
  tone: AvailabilityInterval["tone"];
}

export function AvailabilityCapacityGrid({
  range,
  rows,
  capacity,
  showCapacity,
  capacityWarningThresholdPct,
  capacityTeamSummary,
  renderRowName,
  renderRowActions,
  onIntervalActivate,
  legendItems,
}: AvailabilityCapacityGridProps): React.ReactElement {
  const { t, i18n } = useTranslation();
  const allowedAbsenceCount =
    capacityTeamSummary?.minimumAvailableCount === undefined
      ? 0
      : Math.max(0, capacityTeamSummary.memberCount - capacityTeamSummary.minimumAvailableCount);
  const [activeDayIndex, setActiveDayIndex] = useState<number | null>(null);
  const [rovingDayIndex, setRovingDayIndex] = useState(0);
  const [legendOpen, setLegendOpen] = useState(false);
  const capacityDayRefs = useRef<Array<HTMLDivElement | null>>([]);
  const tooltipId = useId();
  const absenceSummaryId = useId();
  const legendId = useId();
  const dayIndexByKey = useMemo(
    () => new Map(range.days.map((day) => [day.key, day.dayIndex])),
    [range.days],
  );
  const capacityByDate = useMemo(() => new Map(capacity.map((day) => [day.key, day])), [capacity]);
  const today = range.days.find((day) => day.isToday);
  const locale = i18n.resolvedLanguage === "en" ? "en-GB" : "ru-RU";
  const monthFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }),
    [locale],
  );
  const fullDateFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { day: "numeric", month: "long", year: "numeric" }),
    [locale],
  );
  const personDayFormatter = useMemo(
    () => new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }),
    [locale],
  );
  const activeCapacity =
    showCapacity && activeDayIndex !== null ? capacity[activeDayIndex] : undefined;
  const effectiveRovingDayIndex = Math.min(rovingDayIndex, Math.max(0, capacity.length - 1));
  const visibleAbsences = rows.flatMap((row) =>
    row.intervals.flatMap((interval) => {
      const visible = clipInterval(
        interval,
        range.days[0]?.key,
        range.days.at(-1)?.key,
        dayIndexByKey,
      );
      return visible === null ? [] : [{ row, ...visible }];
    }),
  );
  const resolvedLegendItems = useMemo(() => {
    if (legendItems !== undefined) return legendItems;
    const byId = new Map<number | string, AvailabilityLegendItem>();
    for (const interval of rows.flatMap((row) => row.intervals)) {
      const id = interval.eventTypeId ?? interval.eventKey;
      if (!byId.has(id)) {
        byId.set(id, {
          id,
          name: interval.eventName,
          abbreviation: interval.eventAbbreviation,
          tone: interval.tone,
        });
      }
    }
    return [...byId.values()];
  }, [legendItems, rows]);
  const capacityText = (day: CapacityDay): string =>
    day.nominalHours === 0
      ? t("availability.dayOff")
      : t("availability.capacityValue", {
          available: personDayFormatter.format(day.displayAvailablePersonDays),
          total: personDayFormatter.format(day.displayTotalPersonDays),
        });
  const describeInterval = (
    employee: string,
    interval: AvailabilityInterval,
    visibleFrom: string,
    visibleTo: string,
  ): IntervalDescription => {
    const eventName = interval.eventName;
    const from = fullDateFormatter.format(new Date(`${visibleFrom}T12:00:00`));
    const to = fullDateFormatter.format(new Date(`${visibleTo}T12:00:00`));
    const reason = interval.reason.trim();
    const typeText = t("availability.eventTypeReason", { type: eventName });
    const ariaParts = [employee, typeText, `${from} — ${to}`];
    const tooltipLines = [employee, typeText, `${from} — ${to}`];
    if (reason !== "") {
      const reasonText = t("availability.eventComment", { reason });
      ariaParts.push(reasonText);
      tooltipLines.push(reasonText);
    }
    if (interval.isAllDay) {
      const days =
        differenceInCalendarDays(
          new Date(`${interval.toKey}T12:00:00`),
          new Date(`${interval.fromKey}T12:00:00`),
        ) + 1;
      const durationText = t("availability.durationDays", { count: days });
      ariaParts.push(durationText);
      tooltipLines.push(durationText);
    } else if (interval.durationHours > 0) {
      const hoursText = t("availability.durationHours", {
        hours: personDayFormatter.format(interval.durationHours),
      });
      ariaParts.push(hoursText);
      tooltipLines.push(hoursText);
    }
    return { ariaLabel: ariaParts.join(" · "), tooltipLines };
  };
  const employeeStartRow = showCapacity ? 4 : 3;
  const gridStyle: CapacityGridStyle = {
    gridTemplateColumns: `var(--availability-capacity-name-col) repeat(${range.days.length}, var(--availability-capacity-day-track))`,
    gridTemplateRows: `${showCapacity ? "var(--sp-5) var(--sp-5) var(--availability-capacity-capacity-height)" : "var(--sp-5) var(--sp-5)"} repeat(${rows.length}, var(--availability-capacity-row-height))`,
    "--availability-capacity-day-count": range.days.length,
    ...(capacityWarningThresholdPct === undefined
      ? {}
      : { "--availability-capacity-threshold-ratio": capacityWarningThresholdPct / 100 }),
  };
  const moveCapacityFocus = (event: KeyboardEvent<HTMLDivElement>, dayIndex: number): void => {
    let nextIndex: number;
    if (event.key === "ArrowLeft") nextIndex = Math.max(0, dayIndex - 1);
    else if (event.key === "ArrowRight") nextIndex = Math.min(capacity.length - 1, dayIndex + 1);
    else if (event.key === "Home") nextIndex = 0;
    else if (event.key === "End") nextIndex = capacity.length - 1;
    else return;
    event.preventDefault();
    setRovingDayIndex(nextIndex);
    setActiveDayIndex(nextIndex);
    capacityDayRefs.current[nextIndex]?.focus();
  };

  return (
    <TooltipProvider>
      <div
        className="availability-capacity-scroll"
        data-testid="availability-capacity-scroll"
        role="region"
        tabIndex={0}
        aria-label={t("availability.gridLabel")}
        aria-describedby={visibleAbsences.length > 0 ? absenceSummaryId : undefined}
      >
        <div
          className="availability-capacity-grid"
          data-horizon={range.horizon}
          data-show-capacity={String(showCapacity)}
          data-testid="availability-capacity-grid"
          style={gridStyle}
          onPointerLeave={() => setActiveDayIndex(null)}
        >
          <div className="availability-capacity-corner">{t("availability.employeeColumn")}</div>

          {range.monthSpans.map((span) => (
            <div
              key={span.key}
              className="availability-capacity-month"
              data-month={span.key}
              data-testid="availability-capacity-month"
              style={{ gridColumn: `${span.startDayIndex + 2} / span ${span.daySpan}` }}
            >
              {monthFormatter.format(span.anchorDate)}
            </div>
          ))}

          {range.weekSpans.map((span, spanIndex) => {
            const edge =
              range.horizon === 12 &&
              span.daySpan < 7 &&
              (spanIndex === 0 || spanIndex === range.weekSpans.length - 1);
            const hidden = edge && span.daySpan < 4;
            return (
              <div
                key={span.key}
                className="availability-capacity-week"
                data-compact-edge={(edge && !hidden) || undefined}
                data-hidden-edge={hidden || undefined}
                data-testid="availability-capacity-week"
                style={{ gridColumn: `${span.startDayIndex + 2} / span ${span.daySpan}` }}
              >
                {hidden
                  ? null
                  : t(edge ? "availability.weekCompact" : "availability.week", {
                      number: getISOWeek(span.anchorDate),
                    })}
              </div>
            );
          })}

          {showCapacity && (
            <div className="availability-capacity-label">
              <span className="availability-capacity-label__title">
                {t("availability.capacity")}
              </span>
              {capacityTeamSummary !== undefined && (
                <>
                  <span className="availability-capacity-label__detail">
                    {t("availability.teamMemberCount", {
                      count: capacityTeamSummary.memberCount,
                    })}
                  </span>
                  {capacityTeamSummary.minimumAvailableCount !== undefined && (
                    <>
                      <span className="availability-capacity-label__detail">
                        {allowedAbsenceCount === 0
                          ? t("availability.anyAbsenceWarning")
                          : t("availability.allowedAbsenceCount", {
                              count: allowedAbsenceCount,
                            })}
                      </span>
                      {allowedAbsenceCount > 0 && (
                        <span className="availability-capacity-label__detail">
                          {t("availability.warningAbsenceCount", {
                            count: allowedAbsenceCount + 1,
                          })}
                        </span>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {rows.length > 0 &&
            range.days.map((day) => (
              <div
                key={`backdrop-${day.key}`}
                className="availability-capacity-day-backdrop"
                data-day={day.key}
                data-testid="availability-capacity-day-backdrop"
                data-day-kind={capacityByDate.get(day.key)?.dayKind}
                data-weekend={
                  (capacityByDate.get(day.key)?.isWeekend ?? day.isWeekend) || undefined
                }
                style={{
                  gridColumn: day.dayIndex + 2,
                  gridRow: `${employeeStartRow} / span ${rows.length}`,
                }}
                onPointerEnter={() => setActiveDayIndex(day.dayIndex)}
              />
            ))}

          {showCapacity &&
            capacity.map((day) => (
              <div
                key={`capacity-${day.key}`}
                className="availability-capacity-day availability-capacity-day--without-divider"
                data-day={day.key}
                data-testid="availability-capacity-day"
                data-critical={day.isCritical || undefined}
                data-day-kind={day.dayKind}
                data-weekend={day.isWeekend || undefined}
                style={
                  {
                    gridColumn: day.dayIndex + 2,
                    "--capacity-visual-ratio": day.visualRatio,
                  } as GridStyle
                }
                ref={(node) => {
                  capacityDayRefs.current[day.dayIndex] = node;
                }}
                tabIndex={day.dayIndex === effectiveRovingDayIndex ? 0 : -1}
                aria-label={t("availability.tooltip", {
                  date: fullDateFormatter.format(day.date),
                  availability: capacityText(day),
                })}
                aria-describedby={activeDayIndex === day.dayIndex ? tooltipId : undefined}
                onPointerEnter={() => setActiveDayIndex(day.dayIndex)}
                onFocus={() => {
                  setRovingDayIndex(day.dayIndex);
                  setActiveDayIndex(day.dayIndex);
                }}
                onKeyDown={(event) => moveCapacityFocus(event, day.dayIndex)}
                onBlur={() =>
                  setActiveDayIndex((current) => (current === day.dayIndex ? null : current))
                }
              >
                <span className="availability-capacity-bar" aria-hidden="true" />
              </div>
            ))}

          {showCapacity && capacityWarningThresholdPct !== undefined && (
            <div
              className="availability-capacity-threshold-line"
              data-testid="availability-capacity-threshold-line"
              style={{ gridColumn: "2 / -1", gridRow: 3 }}
              aria-hidden="true"
            />
          )}

          {rows.map((row, rowIndex) => (
            <AvailabilityGridRow
              key={row.id}
              item={row}
              row={rowIndex + employeeStartRow}
              dayCount={range.days.length}
              dayIndexByKey={dayIndexByKey}
              rangeStartKey={range.days[0]?.key}
              rangeEndKey={range.days.at(-1)?.key}
              describeInterval={describeInterval}
              renderRowName={renderRowName}
              renderRowActions={renderRowActions}
              onIntervalActivate={onIntervalActivate}
            />
          ))}

          {today && (
            <div
              className="availability-capacity-today"
              data-testid="availability-capacity-today"
              style={{
                gridColumn: today.dayIndex + 2,
                gridRow: `1 / span ${rows.length + employeeStartRow - 1}`,
              }}
              aria-label={t("availability.today")}
            />
          )}

          {activeCapacity && (
            <>
              <div
                className="availability-capacity-hover-column"
                data-testid="availability-capacity-hover-column"
                style={{
                  gridColumn: activeCapacity.dayIndex + 2,
                  gridRow: `1 / span ${rows.length + 3}`,
                }}
                aria-hidden="true"
              />
              <Tooltip open>
                <TooltipTrigger asChild>
                  <div
                    className="availability-capacity-tooltip-anchor"
                    style={{ gridColumn: activeCapacity.dayIndex + 2, gridRow: 3 }}
                    aria-hidden="true"
                  />
                </TooltipTrigger>
                <TooltipContent
                  id={tooltipId}
                  side="bottom"
                  align="start"
                  className="pointer-events-none"
                >
                  {t("availability.tooltip", {
                    date: fullDateFormatter.format(activeCapacity.date),
                    availability: capacityText(activeCapacity),
                  })}
                </TooltipContent>
              </Tooltip>
            </>
          )}
        </div>
      </div>
      {visibleAbsences.length > 0 && (
        <ul id={absenceSummaryId} className="sr-only">
          {visibleAbsences.map(({ row, interval, visibleFrom, visibleTo }, index) => {
            const description = describeInterval(row.name, interval, visibleFrom, visibleTo);
            return (
              <li key={`${row.id}-${interval.eventKey}-${interval.fromKey}-${index}`}>
                {description.ariaLabel}
              </li>
            );
          })}
        </ul>
      )}
      {resolvedLegendItems.length > 0 && (
        <div className="availability-capacity-legend-disclosure">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-expanded={legendOpen}
            aria-controls={legendId}
            onClick={() => setLegendOpen((current) => !current)}
          >
            <ChevronDown
              aria-hidden="true"
              className="availability-capacity-legend-disclosure__chevron"
              data-open={legendOpen || undefined}
            />
            {t(legendOpen ? "availability.legendHide" : "availability.legendShow")}
          </Button>
          {legendOpen && (
            <ul
              id={legendId}
              className="availability-capacity-legend"
              aria-label={t("availability.legend")}
            >
              {resolvedLegendItems.map((item) => (
                <li key={item.id}>
                  <span
                    className="availability-capacity-swatch"
                    style={{
                      backgroundColor: PERSON_EVENT_COLOR_TONE_TOKENS[item.tone].background,
                    }}
                    aria-hidden="true"
                  />
                  <span className="availability-capacity-legend__abbreviation">
                    {item.abbreviation}
                  </span>
                  <span className="availability-capacity-legend__name">{item.name}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </TooltipProvider>
  );
}

interface AvailabilityGridRowProps {
  item: AvailabilityRow;
  row: number;
  dayCount: number;
  dayIndexByKey: Map<string, number>;
  rangeStartKey: string | undefined;
  rangeEndKey: string | undefined;
  describeInterval: (
    employee: string,
    interval: AvailabilityInterval,
    visibleFrom: string,
    visibleTo: string,
  ) => IntervalDescription;
  renderRowName?: (row: AvailabilityRow) => ReactNode;
  renderRowActions?: (row: AvailabilityRow) => ReactNode;
  onIntervalActivate?: (row: AvailabilityRow, interval: AvailabilityInterval) => void;
}

function AvailabilityGridRow({
  item,
  row,
  dayCount,
  dayIndexByKey,
  rangeStartKey,
  rangeEndKey,
  describeInterval,
  renderRowName,
  renderRowActions,
  onIntervalActivate,
}: AvailabilityGridRowProps): React.ReactElement {
  const renderedName = renderRowName?.(item);
  const renderedElement = renderedName as ReactElement<{ className?: string }>;
  const nameTrigger = isValidElement(renderedName) ? (
    cloneElement(renderedElement, {
      className: cn(renderedElement.props.className, "availability-capacity-name__content"),
    })
  ) : (
    <span className="availability-capacity-name__content" tabIndex={0}>
      {renderedName ?? item.name}
    </span>
  );
  return (
    <>
      <div
        className="availability-capacity-name"
        data-testid="availability-capacity-employee"
        style={{ gridRow: row }}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="availability-capacity-name__trigger">{nameTrigger}</span>
          </TooltipTrigger>
          <TooltipContent>{item.fullName}</TooltipContent>
        </Tooltip>
        {renderRowActions?.(item)}
      </div>
      <div
        className="availability-capacity-lane"
        style={{ gridColumn: `2 / span ${dayCount}`, gridRow: row }}
        aria-hidden="true"
      />
      {item.intervals.map((interval, index) => {
        const visible = clipInterval(interval, rangeStartKey, rangeEndKey, dayIndexByKey);
        if (visible === null) return null;
        const { visibleFrom, visibleTo, start, end } = visible;
        const description = describeInterval(item.name, interval, visibleFrom, visibleTo);
        return (
          <AvailabilityIntervalEvent
            key={`${interval.eventKey}-${interval.fromKey}-${index}`}
            interval={interval}
            description={description}
            onActivate={
              interval.canEdit && onIntervalActivate
                ? () => onIntervalActivate(item, interval)
                : undefined
            }
            style={{ gridColumn: `${start + 2} / span ${end - start + 1}`, gridRow: row }}
          />
        );
      })}
    </>
  );
}

interface AvailabilityIntervalEventProps {
  interval: AvailabilityInterval;
  description: IntervalDescription;
  style: CSSProperties;
  onActivate?: () => void;
}

function AvailabilityIntervalEvent({
  interval,
  description,
  style,
  onActivate,
}: AvailabilityIntervalEventProps): React.ReactElement {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  return (
    <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
      <TooltipTrigger asChild>
        <div
          className="availability-capacity-absence"
          data-kind={interval.eventKey}
          data-tone={interval.tone}
          style={{
            ...style,
            backgroundColor: PERSON_EVENT_COLOR_TONE_TOKENS[interval.tone].background,
            color: PERSON_EVENT_COLOR_TONE_TOKENS[interval.tone].foreground,
          }}
          role={onActivate ? "button" : "img"}
          tabIndex={0}
          aria-label={description.ariaLabel}
          onPointerEnter={() => setTooltipOpen(true)}
          onPointerLeave={() => setTooltipOpen(false)}
          onFocus={() => setTooltipOpen(true)}
          onBlur={() => setTooltipOpen(false)}
          onClick={onActivate}
          onKeyDown={(event) => {
            if (!onActivate || (event.key !== "Enter" && event.key !== " ")) return;
            event.preventDefault();
            onActivate();
          }}
        >
          {interval.eventAbbreviation && (
            <span className="availability-capacity-absence__label">
              {interval.eventAbbreviation}
            </span>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent className="availability-capacity-event-tooltip pointer-events-none">
        {description.tooltipLines.map((line) => (
          <div key={line}>{line}</div>
        ))}
      </TooltipContent>
    </Tooltip>
  );
}
